import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertTriangle, AlertOctagon, Activity, RefreshCw } from "lucide-react";
import { API } from "@/lib/site";
import Seo from "@/components/seo/Seo";

const STATUS_META = {
  operational: { label: "Operational", color: "#10B981", icon: CheckCircle2, bg: "bg-emerald-50", text: "text-emerald-700" },
  degraded: { label: "Degraded performance", color: "#F59E0B", icon: AlertTriangle, bg: "bg-amber-50", text: "text-amber-700" },
  outage: { label: "Major outage", color: "#EF4444", icon: AlertOctagon, bg: "bg-rose-50", text: "text-rose-700" },
  maintenance: { label: "Under maintenance", color: "#2563EB", icon: RefreshCw, bg: "bg-sky-50", text: "text-sky-700" },
};

function HistoryBar({ history }) {
  return (
    <div
      className="flex items-center gap-[2px] w-full overflow-hidden"
      data-testid="status-history-bar"
    >
      {history.map((s, i) => (
        <span
          key={i}
          title={`Day -${history.length - i}: ${STATUS_META[s].label}`}
          className="h-7 min-w-[3px] flex-1 rounded-sm"
          style={{ background: STATUS_META[s].color, opacity: 0.85 }}
        />
      ))}
    </div>
  );
}

function impactBadge(impact) {
  const map = {
    none: "bg-slate-100 text-slate-600",
    minor: "bg-amber-50 text-amber-700",
    major: "bg-orange-50 text-orange-700",
    critical: "bg-rose-50 text-rose-700",
  };
  return `inline-flex items-center text-[11px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 ${map[impact] || map.none}`;
}

export default function ApiStatus() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/platform/status`);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError("Unable to load live status.");
    } finally {
      setLoading(false);
    }
  };

  const refresh = () => {
    setLoading(true);
    fetchStatus();
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const res = await axios.get(`${API}/platform/status`);
        if (cancelled) return;
        setData(res.data);
        setError(null);
      } catch (e) {
        if (!cancelled) setError("Unable to load live status.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    const id = setInterval(run, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const overall = data?.overall || "operational";
  const meta = STATUS_META[overall];
  const Icon = meta.icon;

  return (
    <>
      <Seo
        title="Platform Status"
        description="Live status of the AtlasForge Financial APIs — uptime, latency, throughput, and recent incidents across accounts, transactions, webhooks, and every core service."
        path="/status"
      />
      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 lg:px-10 pt-16 pb-10">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">AtlasForge Financial</p>
              <h1
                data-testid="status-title"
                className="font-display mt-2 text-[34px] md:text-[42px] leading-[1.05] text-[#0A1128]"
              >
                Platform Status
              </h1>
            </div>
            <button
              onClick={refresh}
              data-testid="status-refresh"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white text-[#0A1128] text-[13px] font-medium px-4 py-2 hover:border-slate-400 transition-colors"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>

          {/* Overall banner */}
          <div
            data-testid="status-overall-banner"
            className={`mt-8 rounded-2xl border p-6 md:p-8 flex items-start gap-4 ${meta.bg} border-current/10`}
            style={{ borderColor: `${meta.color}33` }}
          >
            <span
              className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-full"
              style={{ background: meta.color, color: "#fff" }}
            >
              <Icon className="h-5 w-5" />
            </span>
            <div className="flex-1">
              <p className={`font-display text-[22px] ${meta.text}`}>
                {data?.headline || "Loading status…"}
              </p>
              <p className="text-[13px] text-slate-600 mt-1">
                {data?.last_checked
                  ? `Last checked ${new Date(data.last_checked).toLocaleString()}`
                  : ""}
                {error && <span className="text-rose-600 ml-2">{error}</span>}
              </p>
            </div>
            <span className="hidden md:inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-slate-500">
              <Activity className="h-4 w-4" /> Auto-refresh · 60s
            </span>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 lg:px-10 pb-16">
          <div className="rounded-2xl border border-slate-200 af-shadow-card divide-y divide-slate-100">
            <div className="px-6 py-4 flex items-center justify-between bg-slate-50 rounded-t-2xl">
              <p className="font-display text-[15px]">Services</p>
              <p className="text-[12px] text-slate-500 uppercase tracking-wider">Last 60 days</p>
            </div>
            {(data?.services || []).map((s) => {
              const m = STATUS_META[s.status];
              const SIcon = m.icon;
              return (
                <div
                  key={s.id}
                  data-testid={`status-service-${s.id}`}
                  className="px-6 py-5 grid md:grid-cols-12 gap-5 items-center"
                >
                  <div className="md:col-span-5">
                    <p className="font-display text-[16px] text-[#0A1128]">{s.name}</p>
                    <p className="text-[12.5px] text-slate-500 mt-1">{s.description}</p>
                  </div>
                  <div className="md:col-span-4">
                    <HistoryBar history={s.history} />
                  </div>
                  <div className="md:col-span-3 flex md:justify-end items-center gap-3 text-[12.5px]">
                    <span className="text-slate-500">{s.uptime_30d.toFixed(2)}% · {s.response_ms}ms</span>
                    <span className={`inline-flex items-center gap-1.5 ${m.bg} ${m.text} text-[12px] font-semibold rounded-full px-2.5 py-1`}>
                      <SIcon className="h-3.5 w-3.5" />
                      {m.label}
                    </span>
                  </div>
                </div>
              );
            })}
            {!data && !error && (
              <div className="px-6 py-10 text-center text-slate-500 text-[14px]">Loading services…</div>
            )}
          </div>
        </div>
      </section>

      <section className="bg-[#F7F9FC] border-y border-slate-200/70">
        <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16">
          <h2 className="font-display text-[24px] md:text-[28px] text-[#0A1128]">Past incidents</h2>
          <div className="mt-6 space-y-3">
            {(data?.incidents || []).map((i) => (
              <div
                key={i.id}
                data-testid={`status-incident-${i.id}`}
                className="rounded-2xl border border-slate-200 bg-white p-5"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={impactBadge(i.impact)}>{i.impact}</span>
                      <span className="text-[11px] uppercase tracking-wider text-slate-500">{i.status}</span>
                    </div>
                    <p className="font-display text-[17px] mt-2">{i.title}</p>
                    <p className="text-[13px] text-slate-600 mt-1 max-w-3xl">{i.summary}</p>
                  </div>
                  <div className="text-[12px] text-slate-500 text-right">
                    <p>Started · {new Date(i.started_at).toLocaleString()}</p>
                    {i.resolved_at && <p>Resolved · {new Date(i.resolved_at).toLocaleString()}</p>}
                  </div>
                </div>
              </div>
            ))}
            {data?.incidents?.length === 0 && (
              <p className="text-slate-500 text-[14px]">No incidents reported in the past 30 days.</p>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
